import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { KNOW_IT_OWLS_LOGO } from "@/data/products";

/* Convert FastAPI error payloads into a printable string. */
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin/orders";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (ex) {
      setError(formatApiErrorDetail(ex?.response?.data?.detail) || ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid-paper flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border-2 border-[#111] bg-[var(--st-cream)] shadow-brutal"
      >
        <header className="border-b-2 border-[#111] bg-[var(--st-maroon)] text-white px-5 py-3 flex items-center gap-3">
          <img src={KNOW_IT_OWLS_LOGO} alt="" className="h-9" />
          <div className="leading-tight">
            <p className="font-display uppercase tracking-tight text-lg">Substitute Teacher</p>
            <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-mustard)]">
              Admin · Order Console
            </p>
          </div>
        </header>

        <form onSubmit={submit} className="p-6 space-y-4" data-testid="admin-login-form">
          <label className="block">
            <span className="font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/70">
              Email
            </span>
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-1 w-full border-2 border-[#111] bg-white px-3 py-2.5 font-body text-base outline-none focus:border-[var(--st-maroon)]"
            />
          </label>
          <label className="block">
            <span className="font-heading uppercase tracking-[0.22em] text-xs text-[var(--st-fg)]/70">
              Password
            </span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-1 w-full border-2 border-[#111] bg-white px-3 py-2.5 font-body text-base outline-none focus:border-[var(--st-maroon)]"
            />
          </label>

          {error && (
            <p
              data-testid="admin-login-error"
              className="font-body text-sm text-[var(--st-maroon)] border-2 border-[var(--st-maroon)]/30 bg-[var(--st-maroon)]/5 px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-5 py-3 font-heading uppercase tracking-[0.18em] text-sm hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] transition-colors disabled:opacity-50 shadow-brutal-sm"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in <ArrowRight size={14} />
              </>
            )}
          </button>

          <div className="pt-2 flex items-center gap-2 justify-center font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-fg)]/60">
            <Lock size={11} /> Admin access only
          </div>
        </form>
      </motion.div>
    </div>
  );
}
